import SEO_ROUTES from "@/data/seoRoutes.json";
import { useSearchParams } from "react-router-dom";
import { Mail, MapPin, Phone, Clock, ExternalLink } from "lucide-react";
import { Seo, breadcrumbSchema, localBusinessSchema } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { EnquiryForm } from "@/components/site/EnquiryForm";
import { WhatsAppButton } from "@/components/site/WhatsAppButton";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import { BUSINESS, SITE_URL } from "@/config/site";
import { PRODUCT_OPTIONS } from "@/data/products";
import { trackEvent } from "@/lib/analytics";

const crumbs = [
  { name: "Home", path: "/" },
  { name: "Contact", path: "/contact" },
];

const fullAddress = `${BUSINESS.address.line1}, ${BUSINESS.address.suburb}, ${BUSINESS.address.city}, ${BUSINESS.address.postalCode}`;
const mapsQuery = encodeURIComponent(`${BUSINESS.address.line1}, ${BUSINESS.address.suburb}, ${BUSINESS.address.city} ${BUSINESS.address.postalCode}, South Africa`);

const ContactCard = ({ icon: Icon, title, children, testid }) => (
  <div className="card-soft flex gap-4 p-5" data-testid={testid}>
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#EEF3EC] text-[color:var(--u-green)]">
      <Icon className="h-5 w-5" aria-hidden="true" />
    </span>
    <div className="min-w-0">
      <h2 className="text-base font-semibold" style={{ fontFamily: "var(--font-body)", letterSpacing: 0 }}>
        {title}
      </h2>
      <div className="mt-1 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </div>
  </div>
);

export default function Contact() {
  const [params] = useSearchParams();
  const qp = params.get("product");
  const defaultProduct = PRODUCT_OPTIONS.some((o) => o.value === qp) ? qp : "general";

  return (
    <>
      <Seo
        title={SEO_ROUTES["/contact"].title}
        description={SEO_ROUTES["/contact"].description}
        path="/contact"
        jsonLd={[breadcrumbSchema(crumbs), localBusinessSchema(), { "@context": "https://schema.org", "@type": "ContactPage", name: "Contact Uhlumo Organics", url: `${SITE_URL}/contact` }]}
      />
      <PageHero crumbs={crumbs} eyebrow="We’d love to hear from you" title="Contact Uhlumo Organics" intro="Ask about products, sizes, pricing, delivery or ordering. WhatsApp is usually the quickest way to reach us.">
        <div className="mt-7 flex flex-wrap gap-3">
          <WhatsAppButton label="WhatsApp Us" testid="contact-hero-whatsapp" location="contact-hero" />
          <a href={`tel:${BUSINESS.phoneE164}`} className="btn btn-outline" onClick={() => trackEvent("phone_click", { location: "contact-hero" })} data-testid="contact-hero-call">
            <Phone className="h-4 w-4" aria-hidden="true" /> Call {BUSINESS.phoneDisplay}
          </a>
        </div>
      </PageHero>

      <section className="section" aria-labelledby="form-heading">
        <div className="container-site grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="card-soft p-6 sm:p-8">
              <h2 id="form-heading" className="text-2xl font-semibold sm:text-3xl">
                Send an enquiry
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">Fields marked * are required. Your details will be added to a WhatsApp message for you to review and send.</p>
              <div className="mt-7">
                <EnquiryForm key={defaultProduct} defaultProduct={defaultProduct} requireSurname idPrefix="contact-form" />
              </div>
            </div>
          </div>
          <div className="space-y-4 lg:col-span-5">
            <ContactCard icon={WhatsAppIcon} title="WhatsApp" testid="contact-card-whatsapp">
              <WhatsAppButton label={`+27 65 377 8765`} className="link-accent inline-flex items-center gap-1" testid="contact-card-whatsapp-link" location="contact-card" />
            </ContactCard>
            <ContactCard icon={Phone} title="Telephone" testid="contact-card-phone">
              <a href={`tel:${BUSINESS.phoneE164}`} className="link-accent" onClick={() => trackEvent("phone_click", { location: "contact-card" })}>
                {BUSINESS.phoneDisplay}
              </a>
            </ContactCard>
            <ContactCard icon={Mail} title="Email" testid="contact-card-email">
              <a href={`mailto:${BUSINESS.email}`} className="link-accent break-all" onClick={() => trackEvent("email_click", { location: "contact-card" })}>
                {BUSINESS.email}
              </a>
            </ContactCard>
            <ContactCard icon={MapPin} title="Address" testid="contact-card-address">
              <address className="not-italic">
                {BUSINESS.address.line1}
                <br />
                {BUSINESS.address.suburb}, {BUSINESS.address.city}, {BUSINESS.address.postalCode}
                <br />
                {BUSINESS.address.province}, {BUSINESS.address.countryName}
              </address>
              <a href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`} target="_blank" rel="noopener noreferrer" className="link-accent mt-1.5 inline-flex items-center gap-1 text-sm">
                Open in Google Maps <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
              <p className="mt-1.5 text-xs">Please contact us before visiting.</p>
            </ContactCard>
            <ContactCard icon={Clock} title="Business hours" testid="contact-card-hours">
              {BUSINESS.hours}
              <br />
              <span className="text-xs">{BUSINESS.hoursNote}</span>
            </ContactCard>
          </div>
        </div>
      </section>

      <section className="pb-20" aria-label="Map">
        <div className="container-site">
          <div className="overflow-hidden rounded-2xl border" style={{ borderColor: "rgba(40,91,69,0.14)" }}>
            <iframe
              title={`Map showing ${fullAddress}`}
              src={`https://www.google.com/maps?q=${mapsQuery}&output=embed`}
              className="h-[320px] w-full sm:h-[380px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </>
  );
}
